import { useConversionTrackingConsent } from "@circle-react/hooks/useConversionTrackingConsent";
import { useEffect, useMemo, useRef } from "react";

import {
  clearFunnelUuid,
  isRedirectReturn,
  mintFunnelUuid,
  persistFunnelUuid,
  resolveFunnelUuid,
} from "./useFunnelUuid";

// PAY-404 -- `CheckoutSessionProvider` does NOT unmount/remount when a
// member navigates client-side to a different checkout page (same TanStack
// Router route definition, re-rendered with new props). `paywallId` is the
// stable, synchronous signal that the *checkout run* changed (unlike
// `session?.id`, which resolves asynchronously and would spuriously look
// "changed" mid-mount) -- when it changes, the funnel UUID must reset.
//
// UUID selection waits for a session; cookie persistence waits for consent.
export const useConversionTrackingFunnelState = (
  paywallId: number,
  isSessionReady: boolean,
) => {
  const isConsentGranted = useConversionTrackingConsent();
  const funnelUuidRef = useRef<string | null>(null);

  const previousPaywallIdRef = useRef<number | null>(null);
  const isNewCheckoutRun =
    previousPaywallIdRef.current !== null &&
    previousPaywallIdRef.current !== paywallId;
  if (isNewCheckoutRun) {
    funnelUuidRef.current = null;
  }
  previousPaywallIdRef.current = paywallId;

  const search = globalThis.location?.search ?? "";
  const isRedirectReturnMount = useMemo(
    () => isRedirectReturn(search),
    [search],
  );
  if (isSessionReady && funnelUuidRef.current === null) {
    funnelUuidRef.current = isConsentGranted
      ? resolveFunnelUuid(search)
      : mintFunnelUuid();
  }
  const funnelUuid = funnelUuidRef.current ?? "";
  useEffect(() => {
    if (!isConsentGranted) {
      clearFunnelUuid();
    } else if (funnelUuid) {
      persistFunnelUuid(funnelUuid);
    }
  }, [funnelUuid, isConsentGranted]);

  return { funnelUuid, isRedirectReturnMount };
};
